import { useMemo } from "react";

/**
 * Abstract geometric network graphic — pure SVG.
 * Represents connectivity between physicians, the PS GLOBAL coordination
 * core, and certified laboratories. No medical clichés (no syringes/pills).
 */
export default function NetworkGraphic() {
  // Outer ring nodes around the center
  const outerNodes = useMemo(() => {
    const count = 8;
    const r = 165;
    return Array.from({ length: count }).map((_, i) => {
      const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
      return {
        x: 250 + r * Math.cos(angle),
        y: 250 + r * Math.sin(angle),
        delay: (i * 0.18).toFixed(2),
      };
    });
  }, []);

  const innerNodes = useMemo(() => {
    const count = 5;
    const r = 88;
    return Array.from({ length: count }).map((_, i) => {
      const angle = (i / count) * Math.PI * 2 + Math.PI / 5;
      return {
        x: 250 + r * Math.cos(angle),
        y: 250 + r * Math.sin(angle),
        delay: (0.4 + i * 0.22).toFixed(2),
      };
    });
  }, []);

  return (
    <div className="relative aspect-square w-full max-w-[560px] mx-auto select-none">
      {/* Soft background glow */}
      <div
        className="absolute inset-6 rounded-full opacity-70"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(15,23,42,0.07) 0%, rgba(15,23,42,0.02) 45%, transparent 70%)",
        }}
      />

      <svg
        viewBox="0 0 500 500"
        className="relative w-full h-full"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="ringGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="#0F172A" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0.18" />
          </linearGradient>
          <linearGradient id="lineGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="#0F172A" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#0F172A" stopOpacity="0.05" />
          </linearGradient>
          <radialGradient id="coreGrad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#0F172A" />
            <stop offset="65%" stopColor="#1E293B" />
            <stop offset="100%" stopColor="#0F172A" />
          </radialGradient>
        </defs>

        {/* Concentric rings — slow orbit */}
        <g className="animate-orbit-slow" style={{ transformOrigin: "250px 250px" }}>
          <circle cx="250" cy="250" r="220" fill="none" stroke="url(#ringGrad)" strokeWidth="1" strokeDasharray="2 8" />
          <circle cx="250" cy="250" r="165" fill="none" stroke="#0F172A" strokeOpacity="0.10" strokeWidth="1" />
        </g>
        <g className="animate-orbit-rev" style={{ transformOrigin: "250px 250px" }}>
          <circle cx="250" cy="250" r="125" fill="none" stroke="#0F172A" strokeOpacity="0.08" strokeWidth="1" strokeDasharray="4 6" />
          <circle cx="250" cy="250" r="88" fill="none" stroke="#059669" strokeOpacity="0.22" strokeWidth="1" />
        </g>

        {/* Lines from center to outer nodes */}
        {outerNodes.map((n, i) => (
          <line
            key={`line-${i}`}
            x1="250"
            y1="250"
            x2={n.x}
            y2={n.y}
            stroke="url(#lineGrad)"
            strokeWidth="1"
          />
        ))}

        {/* Inner ring nodes */}
        {innerNodes.map((n, i) => (
          <g key={`inner-${i}`} className="animate-pulse-dot" style={{ animationDelay: `${n.delay}s` }}>
            <circle cx={n.x} cy={n.y} r="5" fill="#059669" />
            <circle cx={n.x} cy={n.y} r="11" fill="none" stroke="#059669" strokeOpacity="0.25" />
          </g>
        ))}

        {/* Outer nodes — pill capsules (abstract, not medical clichés) */}
        {outerNodes.map((n, i) => (
          <g key={`outer-${i}`} className="animate-pulse-dot" style={{ animationDelay: `${n.delay}s` }}>
            <rect
              x={n.x - 14}
              y={n.y - 7}
              width="28"
              height="14"
              rx="7"
              fill="#FFFFFF"
              stroke="#0F172A"
              strokeWidth="1.25"
            />
            <circle cx={n.x} cy={n.y} r="2.5" fill="#0F172A" />
          </g>
        ))}

        {/* Center core */}
        <g className="animate-float-slow" style={{ transformOrigin: "250px 250px" }}>
          <circle cx="250" cy="250" r="44" fill="url(#coreGrad)" />
          <circle cx="250" cy="250" r="44" fill="none" stroke="#FFFFFF" strokeOpacity="0.10" />
          {/* Central PS monogram */}
          <text
            x="250"
            y="259"
            textAnchor="middle"
            fontFamily="Cabinet Grotesk, sans-serif"
            fontSize="22"
            fontWeight="700"
            fill="#FFFFFF"
            letterSpacing="2"
          >
            PS
          </text>
        </g>

        {/* Accent corner marks */}
        <g stroke="#B45309" strokeWidth="1.5" fill="none">
          <path d="M30 30 L30 56 M30 30 L56 30" />
          <path d="M470 30 L470 56 M470 30 L444 30" />
          <path d="M30 470 L30 444 M30 470 L56 470" />
          <path d="M470 470 L470 444 M470 470 L444 470" />
        </g>
      </svg>

      {/* Floating tag chips */}
      <div className="absolute top-6 -left-3 bg-white border border-slate-200 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 shadow-sm hidden sm:block">
        Verified Vendor
      </div>
      <div className="absolute bottom-10 -right-2 bg-slate-900 text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold shadow-sm hidden sm:block">
        Audited Manufacturer
      </div>
      <div className="absolute top-1/2 -right-4 bg-white border border-emerald-600/40 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700 shadow-sm hidden sm:block">
        Sourcing Core
      </div>
    </div>
  );
}
